/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Toast } from '@hb/coffee';
import NativeApi from '@/api/native';
import BioApi from '@/api/bio';
import { startFinanceOCRVerify, getBase64ByPath, getOCRBase64 } from '@/shared/tools/ocr/utils/native';
import { MODE_STATUS_METHODS_MAP } from '@/shared/tools/ocr/utils/status';
import { allSettledPolyfill } from '@/shared/tools/ocr/utils/polyfill';
import {
  TCE_OCR_START,
  TCE_OCR_CLIENT_BRIDGE_REQUEST,
  TCE_OCR_CLIENT_BRIDGE_RESPONSE,
  TCE_OCR_BRIDGE_SUCCESS,
  TCE_OCR_BRIDGE_ERROR,
  TCE_OCR_RESULT,
} from '@/shared/tools/ocr/tianqi/scan';
import { ubtCustomEventNewOcr } from '@/shared/tools/ocr/tianqi';
import { toUnixTime } from '@/shared/utils/day';

import {
  OCRModeEnum,
  UploadTypeEnum,
  UploadStatusEnum,
  UploadSceneEnum,
  UploadProcessTypeEnum,
  OcrProcessSourceEnum,
  OcrOperationSourceEnum,
} from '@/shared/tools/ocr/enum';
import type { NativeOCRFront, NativeOCRBack } from '@/api/native/types';
import type { OCRFrontInfo, OCRBackInfo, OCRBothInfo } from '@/shared/tools/ocr/types';

/** 在 sharp-request 里返回code不为0, msg为空的情况，会把msg重置为 「请求失败，请稍后重试」 */
const RESPONSE_EMPTY_MESSAGE = '请求失败，请稍后重试';
const PROMISE_RESOLVE = 'fulfilled';
const PROMISE_REJECT = 'rejected';
/** 不同识别模式，超时对应的状态码 */
const MODE_TIMEOUT_MAP = {
  [OCRModeEnum.BOTH]: UploadStatusEnum.TIMEOUT_BOTH,
  [OCRModeEnum.FRONT]: UploadStatusEnum.TIMEOUT_FRONT,
  [OCRModeEnum.BACK]: UploadStatusEnum.TIMEOUT_BACK,
};

/** 数据清洗，将接口数据转成 OCRFrontInfo 或 OCRBackInfo */
export const filterOcrInfo = (
  ocrInfo: NativeOCRFront | NativeOCRBack,
  ossCardUrl: string,
): OCRFrontInfo | OCRBackInfo | undefined => {
  let filterInfo: OCRFrontInfo | OCRBackInfo | undefined;

  const frontOcrInfo = ocrInfo as NativeOCRFront;
  if (frontOcrInfo.name) {
    filterInfo = {
      idCardFrontUrl: ossCardUrl,
      idCardNo: frontOcrInfo.number,
      userAddress: frontOcrInfo.address,
      userBirth: frontOcrInfo.birth,
      userName: frontOcrInfo.name,
      userNation: frontOcrInfo.nationality,
      userSex: frontOcrInfo.sex,
      frontTransactionId: frontOcrInfo.frontTransactionId,
    };
  }

  const backOcrInfo = ocrInfo as NativeOCRBack;
  if (backOcrInfo.authority) {
    filterInfo = {
      idCardBackUrl: ossCardUrl,
      issues: backOcrInfo.authority,
      periodBegin: toUnixTime(backOcrInfo.startDate),
      periodEnd: toUnixTime(backOcrInfo.endDate),
      backTransactionId: backOcrInfo.backTransactionId,
    };
  }

  return filterInfo;
};

/** 通过base64图片信息通过ocr api获取身份证信息 */
export const getOcrInfoByBase64 = async (
  base64: string,
  uploadType: UploadTypeEnum,
  /** 防止oss并发调用时，文件同名 */
  scene: string,
  /** ocr流程来源 */
  processSource: OcrProcessSourceEnum,
  /** 识别操作来源 */
  operationSource: OcrOperationSourceEnum,
): Promise<OCRFrontInfo | OCRBackInfo | undefined> => {
  // 调用f.oss.policy.user图片上传到oss, scene字段 user-id0 或 user-id1
  // 两张图片并发上传时，需要一张分配为user-id0，另一张分配为user-id1
  const ossUrl = await BioApi.uploadIdCard(base64, scene);
  // 调用f.fate.ocr.ocr识别图片内容, scene字段非必传
  const ocrInfo = await BioApi.onlineOCR(
    {
      pic: ossUrl,
      uploadType,
      processType: UploadProcessTypeEnum.SCAN,
      processSource,
      operationSource,
    },
    {
      showLoading: undefined,
      showErrMsg: undefined,
    },
  );
  // 数据清洗，拿到展示信息
  const filterInfo = filterOcrInfo(ocrInfo, ossUrl);

  return filterInfo;
};

/** 获取接口返回的错误信息 */
export const getResponseErrorMessage = (res: PromiseRejectedResult): string => {
  if ('message' in res.reason && res.reason.message !== RESPONSE_EMPTY_MESSAGE) {
    return res.reason.message as string;
  }

  return '';
};

/** 相册上传 */
export const captureIdentityByAlbum = async (mode: OCRModeEnum): Promise<string[]> => {
  const maxSelectNum = mode === OCRModeEnum.BOTH ? 2 : 1;
  // Android 取消相册选择可以正常回调
  // IOS 取消相册选择无回调
  const selectPhotos = await NativeApi.selectPhotos(maxSelectNum);
  const length = selectPhotos && selectPhotos.length ? selectPhotos.length : 0;
  if (length === 1) {
    const { path, width, height } = selectPhotos[0];
    const base64Src = await getBase64ByPath(path, width, height);
    return [base64Src];
  }

  if (length === 2) {
    const { path: firstPath, width: firstWidth, height: firstHeight } = selectPhotos[0];
    const { path: secondPath, width: secondWidth, height: secondHeight } = selectPhotos[1];
    const base64SrcFirst = await getBase64ByPath(firstPath, firstWidth, firstHeight);
    const base64SrcSecond = await getBase64ByPath(secondPath, secondWidth, secondHeight);

    return [base64SrcFirst, base64SrcSecond];
  }

  return [];
};

/** 拍摄上传 */
export const captureIdentityByCamera = async (mode: OCRModeEnum): Promise<string[]> => {
  const signInfo = await BioApi.getTencentSDKSign();

  /** 客户端OCR发起埋点 */
  ubtCustomEventNewOcr(TCE_OCR_CLIENT_BRIDGE_REQUEST, {
    mode,
    ...signInfo,
  });

  const res = await startFinanceOCRVerify({
    appId: signInfo.appId,
    userId: signInfo.userId,
    version: signInfo.version,
    orderNo: signInfo.orderNo,
    nonce: signInfo.nonceStr,
    sign: signInfo.signature,
    mode,
  });

  const { code, data, msg } = res || {};

  const {
    idCardResult,
    frontImageWidth = 1000, frontImageHeight = 568,
    backImageWidth = 1000, backImageHeight = 568,
  } = data || {};
  const {
    frontFullImageSrc,
    backFullImageSrc,
    cardNum,
    frontClarity,
    frontMultiWarning,
    office,
    backClarity,
    backMultiWarning,
    ocrId,
  } = idCardResult || {};

  /** 客户端OCR响应埋点 */
  ubtCustomEventNewOcr(TCE_OCR_CLIENT_BRIDGE_RESPONSE, {
    mode,
    code,
    msg,
    frontFullImageSrc,
    backFullImageSrc,
    cardNum,
    frontClarity,
    frontMultiWarning,
    office,
    backClarity,
    backMultiWarning,
    ocrId,
    orderNo: signInfo.orderNo,
  });

  // 将图片的最大尺寸调整为1000，防止OCR API服务上传时，图片不清晰导致失败
  let base64SrcFront;
  let base64SrcBack;
  if (frontFullImageSrc) {
    base64SrcFront = await getOCRBase64(
      frontFullImageSrc,
      frontImageWidth,
      frontImageHeight,
    );
  }
  if (backFullImageSrc) {
    base64SrcBack = await getOCRBase64(
      backFullImageSrc,
      backImageWidth,
      backImageHeight,
    );
  }

  if (base64SrcFront && base64SrcBack) {
    return [base64SrcFront, base64SrcBack];
  }
  if (base64SrcFront && !base64SrcBack) {
    return [base64SrcFront];
  }
  if (!base64SrcFront && base64SrcBack) {
    return [base64SrcBack];
  }

  return [];
};

/** 统一获取身份证信息入口 */
export const captureIdentity = async (
  uploadType: UploadTypeEnum,
  mode: OCRModeEnum,
  processSource: OcrProcessSourceEnum,
  operationSource: OcrOperationSourceEnum,
): Promise<OCRBothInfo> => new Promise(async (resolve) => {
  // 发起埋点，uploadType/mode
  ubtCustomEventNewOcr(TCE_OCR_START, {
    uploadType,
    mode,
  });

  let base64SrcFirst: string | undefined;
  let base64SrcSecond: string | undefined;
  try {
    if (uploadType === UploadTypeEnum.Album) {
      [base64SrcFirst, base64SrcSecond] = await captureIdentityByAlbum(mode);
    } else {
      [base64SrcFirst, base64SrcSecond] = await captureIdentityByCamera(mode);
    }

    // 拍摄 或 相册 Bridge成功
    ubtCustomEventNewOcr(TCE_OCR_BRIDGE_SUCCESS, {
      uploadType,
      mode,
      // 1表示有图，0表示无图
      imageFirst: base64SrcFirst ? '1' : '0',
      imageSecond: base64SrcSecond ? '1' : '0',
    });
  } catch (error) {
    let errorMessage = '';

    try {
      errorMessage = String(error);
    } catch {
      // 无需处理
    }

    // 拍摄 或 相册 Bridge失败
    ubtCustomEventNewOcr(TCE_OCR_BRIDGE_ERROR, {
      uploadType,
      mode,
      errorMessage,
    });
  }


  let result: OCRBothInfo = {
    uploadType,
    mode,
    status: UploadStatusEnum.PROCESSING,
  };

  // 未获取到任何图片，超时处理
  if (!base64SrcFirst && !base64SrcSecond) {
    Toast.clear();
    Toast.fail('识别失败');

    result.status = MODE_TIMEOUT_MAP[mode];

    // 结果埋点，result信息
    ubtCustomEventNewOcr(TCE_OCR_RESULT, {
      uploadType: result.uploadType,
      mode: result.mode,
      status: result.status,
    });

    resolve(result);
    return;
  }

  const firstPromise = base64SrcFirst
    ? getOcrInfoByBase64(
      base64SrcFirst,
      uploadType,
      UploadSceneEnum.ID_CARD_FRONT,
      processSource,
      operationSource,
    )
    : Promise.resolve(undefined);
  const secondPromise = base64SrcSecond
    ? getOcrInfoByBase64(
      base64SrcSecond,
      uploadType,
      UploadSceneEnum.ID_CARD_BACK,
      processSource,
      operationSource,
    )
    : Promise.resolve(undefined);

  allSettledPolyfill();

  Toast.loading({
    message: '信息识别中...',
    duration: 15000,
    forbidClick: true,
  });

  Promise.allSettled([
    firstPromise,
    secondPromise,
  ]).then((res) => {
    if (res.length >= 2) {
      const firstRes = res[0];
      const secondRes = res[1];
      let successCount = 0;

      /** 第一张图请求成功处理，人像面数据 或 国徽面数据 */
      if (firstRes.status === PROMISE_RESOLVE && firstRes.value) {
        successCount += 1;
        const ocrInfo = firstRes.value as OCRBothInfo;
        result = {
          ...result,
          ...ocrInfo,
        };
      }

      /** 第二张图请求成功处理，人像面数据 或 国徽面数据 */
      if (secondRes.status === PROMISE_RESOLVE && secondRes.value) {
        successCount += 1;
        const ocrInfo = secondRes.value as OCRBothInfo;
        result = {
          ...result,
          ...ocrInfo,
        };
      }

      /** 第一张图请求失败处理 */
      if (firstRes.status === PROMISE_REJECT && firstRes.reason) {
        result.errorMessage = getResponseErrorMessage(firstRes);
      }

      /** 第二张图请求失败处理 */
      if (secondRes.status === PROMISE_REJECT && secondRes.reason) {
        result.errorMessage = getResponseErrorMessage(secondRes);
      }

      /** 本次识别状态处理 */
      result.status = MODE_STATUS_METHODS_MAP[mode]({
        base64SrcFirst,
        base64SrcSecond,
        uploadType,
        result,
        successCount,
      });
    }

    if (![UploadStatusEnum.SUCCESS, UploadStatusEnum.NOT_MATCH].includes(result.status)) {
      Toast.clear();
      Toast.fail('识别失败');
    } else {
      Toast.clear();
    }

    // 调试与测试使用，暂时不用删除
    // eslint-disable-next-line no-console
    console.log('OCR识别结果', result);

    // 结果埋点，result信息
    ubtCustomEventNewOcr(TCE_OCR_RESULT, {
      uploadType: result.uploadType,
      mode: result.mode,
      status: result.status,
      errorMessage: result.errorMessage,
    });

    resolve(result);
  });
});
